import React, { useMemo } from "react";
import { Box, Typography, Avatar, Tooltip } from "@mui/material";
import {
  OpportunityRow,
  OpportunityStakeholder,
  STAKEHOLDER_ROLE_LABELS,
} from "../../interfaces/opportunities";
import {
  formatArrAmount,
  formatNextMeeting,
  validateDate,
  getAvatarColor,
  getAvatarInitials,
  capitalizeFirstLetter
} from "../../utils/utils";

interface OpportunityDetailsGridProps {
  opportunity: OpportunityRow | null;
  stakeholders?: OpportunityStakeholder[];
}

const OpportunityDetailsGrid: React.FC<OpportunityDetailsGridProps> = ({
  opportunity,
  stakeholders,
}) => {
  // Parse stakeholders from opportunity.stakeholders map if present
  const apiStakeholders = useMemo<OpportunityStakeholder[]>(() => {
    if (!opportunity?.stakeholders) return [];
    const list: OpportunityStakeholder[] = [];

    Object.entries(opportunity.stakeholders).forEach(([key, person]) => {
      if (person?.fullName) {
        list.push({
          id: key,
          name: person.fullName,
          role: STAKEHOLDER_ROLE_LABELS[key] || person.designation || undefined,
          avatarUrl: person.profilePictureUrl || undefined,
          email: person.email,
        });
      }
    });
    return list;
  }, [opportunity?.stakeholders]);

  const effectiveStakeholders =
    apiStakeholders.length > 0
      ? apiStakeholders
      : stakeholders && stakeholders.length > 0
      ? stakeholders
      : [];

  const arrFormatted =
    opportunity?.arrAmount === null ||
    opportunity?.arrAmount === undefined ||
    opportunity?.arrAmount === ""
      ? "---"
      : formatArrAmount(opportunity.arrAmount);

  const nextMeetingFormatted = opportunity?.nextMeeting
    ? formatNextMeeting(opportunity.nextMeeting) || "---"
    : "---";

  const closeDateFormatted = opportunity?.closeDate
    ? validateDate(opportunity.closeDate, "MMM D, YYYY") || "---"
    : "---";

  return (
    <Box className="opportunity-mid-section">
      {/* Left: Opportunity Details */}
      <Box className="opportunity-details-section">
        <Typography className="opportunity-section-title">
          Opportunity Details :
        </Typography>
        <Box className="opportunity-details-grid">
          <Box className="opportunity-detail-item">
            <span className="opportunity-detail-label">OPPORTUNITY OWNER</span>
            <span className="opportunity-detail-value">
              {opportunity?.opportunityOwner || "---"}
            </span>
          </Box>
          <Box className="opportunity-detail-item">
            <span className="opportunity-detail-label">DEAL STAGE</span>
            <span className="opportunity-detail-value">
              {opportunity?.dealStage || "---"}
            </span>
          </Box>
          <Box className="opportunity-detail-item">
            <span className="opportunity-detail-label">ACCOUNT NAME</span>
            <span className="opportunity-detail-value">
              {capitalizeFirstLetter(opportunity?.account ?? "") || "---"}
            </span>
          </Box>
          <Box className="opportunity-detail-item">
            <span className="opportunity-detail-label">ARR AMOUNT</span>
            <span className="opportunity-detail-value">{arrFormatted}</span>
          </Box>
          <Box className="opportunity-detail-item">
            <span className="opportunity-detail-label">CLOSE DATE</span>
            <span className="opportunity-detail-value">
              {closeDateFormatted}
            </span>
          </Box>
          <Box className="opportunity-detail-item">
            <span className="opportunity-detail-label">MANAGER FORECAST</span>
            <span className="opportunity-detail-value">
              {opportunity?.managerForecast || "---"}
            </span>
          </Box>
          <Box className="opportunity-detail-item">
            <span className="opportunity-detail-label">NEXT MEETING</span>
            <span className="opportunity-detail-value">
              {nextMeetingFormatted}
            </span>
          </Box>
        </Box>
      </Box>

      {/* Right: Stakeholders */}
      <Box className="opportunity-stakeholders-panel">
        <Typography className="opportunity-section-title">
          Opportunity Team :
        </Typography>
        <Box className="opportunity-stakeholders-grid">
          {effectiveStakeholders.length > 0 ? (
            effectiveStakeholders.map((person) => (
              <Box key={person.id} className="opportunity-stakeholder-card">
                <Tooltip
                  title={person.email || ""}
                  arrow
                  placement="top"
                  disableHoverListener={!person.email}
                >
                  <Avatar
                    src={person.avatarUrl}
                    alt={person.name}
                    sx={{
                      width: 34,
                      height: 34,
                      fontSize: 13,
                      fontWeight: 600,
                      bgcolor: getAvatarColor(person.name),
                    }}
                  >
                    {!person.avatarUrl && getAvatarInitials(person.name)}
                  </Avatar>
                </Tooltip>
                <Box className="opportunity-stakeholder-info">
                  <Tooltip
                    title={person.email || ""}
                    arrow
                    placement="top"
                    disableHoverListener={!person.email}
                  >
                    <span className="opportunity-stakeholder-name">
                      {person.name}
                    </span>
                  </Tooltip>
                  {person.role && (
                    <span className="opportunity-stakeholder-role">
                      {person.role}
                    </span>
                  )}
                </Box>
              </Box>
            ))
          ) : (
            <Typography sx={{ fontSize: "13px", color: "#64748B" }}>
              ---
            </Typography>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default OpportunityDetailsGrid;
